// GtCandidatesTable — the Candidates centre screen: schema-driven table + filter rail
// + saved views + column chooser + tags + CSV export. Mechanics ported from CE
// CrmContacts (read end to end 21 Jul 2026). GTCO changes: fully server-driven through
// /api/filter/candidates (no dual client/server path), recruiting search fields,
// Upload CV kept as the primary action, single-tenant.
import { useState, useEffect, useCallback, useRef } from 'react';
import API_BASE from '../../config';
import GtTable from '../../components/GtTable';
import { SearchHint, splitSearch, searchCondition, searchSort } from '../../components/gtSearch'; // C1-SEARCH
import { CANDIDATE_COLUMNS, ALL_CANDIDATE_COLUMNS } from '../../components/gtColumns';
import GtFilterRail from '../../components/GtFilterRail';
import { CANDIDATE_SCHEMA } from '../../components/gtFilterSchema';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import GtCandidateRecord from './GtCandidateRecord';
import { paths } from '../../components/gtRoutes';
import GtAddToBenchModal from '../../components/GtAddToBenchModal';
import GtImportWizard from '../../components/GtImportWizard';
import { GtEnrichModal, GtEnrichProgress, useEnrichFetch } from '../../components/GtEnrich';
import { BulkTagModal, BulkRoleModal, BulkInterviewModal, BulkLookingModal, MergeModal } from '../../components/GtCandidateBulk';
import GtSendFormModal from '../../components/GtSendFormModal';
import GtEmailModal from '../../components/GtEmailModal'; // C1-EMAIL
import GtSequenceStart from '../../components/GtSequenceStart'; // C1-SEQ3

export default function GtCandidatesTable({ getToken, userName, userImage }) {
  // The open record is the URL, not local state, so refresh lands where you were.
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = candidateId || null;
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchMode, setSearchMode] = useState('auto'); // C1-SEARCH
  const [searchInfo, setSearchInfo] = useState(null);
  const routeLoc = useLocation();
  // ?q= from search everywhere ("See all ... in the list") fills the box once.
  useEffect(() => {
    const sp = new URLSearchParams(routeLoc.search);
    const v = sp.get('q');
    if (v == null) return;
    setQuery(v); setDebouncedQuery(v.trim()); setSearchMode('auto');
    sp.delete('q');
    navigate({ pathname: routeLoc.pathname, search: sp.toString() ? '?' + sp.toString() : '' }, { replace: true });
  }, [routeLoc.search]); // eslint-disable-line react-hooks/exhaustive-deps
  // A saved view can hold a search: it goes back into the box, the rest into the rail.
  function applyViewTree(t) {
    const s = splitSearch(t);
    setFilterTree(s.tree); setQuery(s.q); setDebouncedQuery(s.q.trim()); setSearchMode(s.mode);
  }
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [benchFor, setBenchFor] = useState(null);
  const [importing, setImporting] = useState(false);
  const [enrichFor, setEnrichFor] = useState(null);
  const [bulk, setBulk] = useState(null);
  const [selectingAll, setSelectingAll] = useState(false);
  const [enrichRuns, setEnrichRuns] = useState([]);
  const enrichFetch = useEnrichFetch(getToken);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState(() => CANDIDATE_COLUMNS.map(c => c.key));
  const [draftKeys, setDraftKeys] = useState(() => CANDIDATE_COLUMNS.map(c => c.key));
  const [dragKey, setDragKey] = useState(null);

  const [filterTree, setFilterTree] = useState(null);
  const [sort, setSort] = useState({ field: 'created_at', dir: 'desc' });
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [distincts, setDistincts] = useState({});
  const [tags, setTags] = useState([]);
  const [tagMgrOpen, setTagMgrOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const authFetch = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    const res = await fetch(API_BASE + path, {
      ...opts,
      headers: { ...(opts.headers || {}), Authorization: 'Bearer ' + token },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.detail || body.message || body.error || ('request failed: ' + res.status));
    return body;
  }, [getToken]);

  useEffect(() => { if (showColumns) setDraftKeys(visibleKeys); }, [showColumns]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(() => setDebouncedQuery(query.trim()), 250); return () => clearTimeout(t); }, [query]);

  const filterActive = !!(filterTree && Array.isArray(filterTree.and) && filterTree.and.length > 0);

  // Compose the effective tree: rail tree AND quick-search OR-block.
  const effectiveTree = useCallback(() => {
    const parts = [];
    if (filterTree && Array.isArray(filterTree.and)) parts.push(...filterTree.and);
    const sc = searchCondition(debouncedQuery, searchMode); // C1-SEARCH: the whole record, typos, Boolean
    if (sc) parts.push(sc);
    return parts.length ? { and: parts } : null;
  }, [filterTree, debouncedQuery, searchMode]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await authFetch('/api/filter/candidates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tree: effectiveTree(), sort: searchSort(sort, debouncedQuery), limit: pageSize, offset: page * pageSize }),
      });
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setSearchInfo(data.search || null);
    } catch (e) { setError(e.message); setRows([]); setTotal(0); }
    finally { setLoading(false); }
  }, [authFetch, effectiveTree, sort, page, pageSize, debouncedQuery]);
  useEffect(() => { load(); }, [load]);
  // A ?preset= arriving from Home's work queue seeds the filter tree once, then
  // clears itself from the URL so the filter can be edited or removed normally.
  useEffect(() => {
    const preset = searchParams.get('preset');
    if (!preset) return;
    const trees = {
      flagged: { and: [{ field: 'has_parse_flags', op: 'eq', value: true }] },
      rtw_unknown: { and: [{ field: 'right_to_work_status', op: 'is_null' }] },
      no_consent: { and: [{ field: 'consent_status', op: 'in', values: ['none'] }] },
    };
    if (trees[preset]) {
      setFilterTree(trees[preset]);
      setShowFilters(true);
    }
    searchParams.delete('preset');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);
  // Returning from a record refreshes the list so edits made there are visible.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !openId) load();
    wasOpen.current = !!openId;
  }, [openId, load]);
  useEffect(() => { setPage(0); }, [debouncedQuery, pageSize, filterTree]);

  const loadTags = useCallback(async () => {
    try { setTags(await authFetch('/api/candidate-tags')); } catch (e) { /* non-fatal */ }
  }, [authFetch]);
  const loadSavedViews = useCallback(async () => {
    try { setSavedViews(await authFetch('/api/saved-filters/candidates')); } catch (e) { /* non-fatal */ }
  }, [authFetch]);
  const loadDistincts = useCallback(async () => {
    const keys = [...new Set((CANDIDATE_SCHEMA.fields || []).filter(f => f.dropdown).map(f => f.dropdown))];
    const out = {};
    await Promise.all(keys.map(async k => {
      try { out[k] = await authFetch('/api/filter/candidates/distinct/' + encodeURIComponent(k)); } catch (e) { /* skip */ }
    }));
    setDistincts(out);
  }, [authFetch]);
  useEffect(() => { loadTags(); loadSavedViews(); loadDistincts(); }, [loadTags, loadSavedViews, loadDistincts]);

  function flash(msg) { setToast(msg); setTimeout(() => setToast(null), 2600); }

  const createTag = useCallback(async (name, colour) => {
    try { await authFetch('/api/candidate-tags', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, colour }) }); await loadTags(); await loadDistincts(); }
    catch (e) { flash(e.message); }
  }, [authFetch, loadTags, loadDistincts]);
  const deleteTag = useCallback(async (id) => {
    try { await authFetch('/api/candidate-tags/' + id, { method: 'DELETE' }); await loadTags(); await loadDistincts(); await load(); }
    catch (e) { flash(e.message); }
  }, [authFetch, loadTags, loadDistincts, load]);
  const toggleTag = useCallback(async (candidateId, tagId, isOn) => {
    try {
      if (isOn) await authFetch(`/api/candidates/${candidateId}/tags/${tagId}`, { method: 'DELETE' });
      else await authFetch(`/api/candidates/${candidateId}/tags`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tag_id: tagId }) });
      await load();
    } catch (e) { flash(e.message); }
  }, [authFetch, load]);

  async function saveCurrentView(name, shared) {
    try {
      await authFetch('/api/saved-filters/candidates', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, tree: effectiveTree() || filterTree, shared: !!shared }) });
      loadSavedViews();
    } catch (e) { flash(e.message); }
  }
  async function deleteView(viewId) {
    try {
      await authFetch('/api/saved-filters/' + viewId, { method: 'DELETE' });
      if (activeViewId === viewId) { setActiveViewId(null); setFilterTree(null); }
      loadSavedViews();
    } catch (e) { flash(e.message); }
  }

  async function uploadCv(file) {
    if (!file) return;
    if (!/\.(pdf|docx)$/i.test(file.name)) { flash('Upload a PDF or Word (.docx) CV.'); if (fileRef.current) fileRef.current.value = ''; return; }
    setUploading(true);
    try {
      const token = await getToken();
      const fd = new FormData();
      fd.append('cv', file);
      const res = await fetch(API_BASE + '/api/candidates/upload-cv', { method: 'POST', headers: { Authorization: 'Bearer ' + token }, body: fd });
      const body = await res.json().catch(() => ({}));
      if (res.status === 409) { flash('Already in the pool: ' + (body.existing?.full_name || body.existing?.email || 'existing candidate')); }
      else if (!res.ok) { throw new Error(body.detail || body.error || 'upload failed'); }
      else { flash('Candidate added'); }
      await load();
    } catch (e) { flash(e.message); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  }

  const onSort = (field) => {
    setSort(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'desc' });
    setPage(0);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages - 1);

  const toggle = (rowId) => setSelectedIds(prev => { const n = new Set(prev); n.has(rowId) ? n.delete(rowId) : n.add(rowId); return n; });
  const toggleAll = () => setSelectedIds(prev => {
    const on = rows.length > 0 && rows.every(r => prev.has(r.id));
    if (on) return new Set();
    return new Set(rows.map(r => r.id));
  });

  // Ticks everyone the current filter and search match, not just this page.
  const selectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const r = await authFetch('/api/filter/candidates/resolve-ids', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filter: effectiveTree() || { and: [] } }),
      });
      setSelectedIds(new Set(r.ids || []));
    } catch (e) { flash(e.message); }
    setSelectingAll(false);
  };

  const ukDate = (v) => { if (!v) return ''; const d = new Date(v); return isNaN(d) ? '' : d.toLocaleDateString('en-GB'); };
  const cellToText = (col, row) => {
    const v = row[col.key];
    switch (col.render) {
      case 'candidate': return row.full_name || '';
      case 'tags': { const arr = Array.isArray(v) ? v : []; return arr.map(t => t.name).filter(Boolean).join('; '); }
      case 'rate': { const n = row.expected_rate_gbp; return n != null ? `${n} ${row.expected_rate_unit || ''}`.trim() : ''; }
      case 'cv': return row.cv_file_path ? (row.has_parse_flags ? 'Flagged' : 'Parsed') : 'None';
      case 'countpill': return String(v ?? 0);
      case 'ukdate': return ukDate(v);
      default: return (v == null) ? '' : String(v);
    }
  };
  const csvEscape = (s) => { const t = String(s == null ? '' : s); return /[",\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t; };
  const exportCsv = async (onlyIds) => {
    const picked = Array.isArray(onlyIds) && onlyIds.length ? onlyIds : null;
    if (picked && picked.length > 500) flash('Exported the first 500 selected.');
    const cols = visibleKeys.map(k => ALL_CANDIDATE_COLUMNS.find(c => c.key === k)).filter(Boolean);
    let exportRows = [];
    try {
      const data = await authFetch('/api/filter/candidates', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tree: picked ? { or: picked.slice(0, 500).map(id => ({ field: 'id', op: 'eq', value: id })) } : effectiveTree(), sort, limit: 500, offset: 0 }),
      });
      exportRows = data.rows || [];
    } catch (e) { flash(e.message); return; }
    const header = cols.map(c => csvEscape(c.label)).join(',');
    const body = exportRows.map(r => cols.map(c => csvEscape(cellToText(c, r))).join(',')).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `candidates-${picked ? 'selected-' : ''}${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (openId) return <GtCandidateRecord candidateId={openId} getToken={getToken} userName={userName} userImage={userImage} onBack={() => navigate(paths.candidates())} />;

  return (
    <div className="gtc-wrap">
      <div className="gtc-head">
        <div>
          <h1 className="gtc-title">Candidates</h1>
          <p className="gtc-sub">{total} in the pool{filterActive || debouncedQuery ? ' matching' : ''}</p>
        </div>
        <div className="gtc-head-right">
          <button type="button" className="gtc-btn-ghost" onClick={() => setImporting(true)}>Import CSV</button>
          <input ref={fileRef} type="file" accept=".pdf,.docx" style={{ display: 'none' }} onChange={e => uploadCv(e.target.files[0])} />
          <button className="gtc-primary" type="button" disabled={uploading} onClick={() => fileRef.current && fileRef.current.click()}>
            {uploading ? 'Uploading…' : 'Upload CV'}
          </button>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="gtbn-bulk">
          <b>{selectedIds.size} selected</b>
          <button className="gtc-primary" type="button" onClick={() => setBulk({ kind: 'role', ids: Array.from(selectedIds) })}>Add to role</button>
          <button className="gtc-btn-ghost" type="button" onClick={() => setBenchFor(Array.from(selectedIds))}>Add to bench</button>
          <button className="gtc-btn-ghost" type="button" onClick={() => setBulk({ kind: 'interview', ids: Array.from(selectedIds) })}>Invite to interview</button>
          <button className="gtc-btn-ghost" type="button" onClick={() => setBulk({ kind: 'email', ids: Array.from(selectedIds) })}>Email</button>{/* C1-EMAIL */}
          <button className="gtc-btn-ghost" type="button" onClick={() => setBulk({ kind: 'sequence', ids: Array.from(selectedIds) })}>Start sequence</button>{/* C1-SEQ3 */}
          <button className="gtc-btn-ghost" type="button" onClick={() => setBulk({ kind: 'form', ids: Array.from(selectedIds) })}>Send form</button>
          <button className="gtc-btn-ghost" type="button" onClick={() => setBulk({ kind: 'tag', ids: Array.from(selectedIds) })}>Tag</button>
          <button className="gtc-btn-ghost" type="button" onClick={() => setEnrichFor(Array.from(selectedIds))}>Enrich</button>
          <button className="gtc-btn-ghost" type="button" onClick={() => setBulk({ kind: 'looking', ids: Array.from(selectedIds) })}>Not looking</button>
          <button className="gtc-btn-ghost" type="button" onClick={() => exportCsv(Array.from(selectedIds))}>Export</button>
          {selectedIds.size === 2 && (
            <button className="gtc-btn-ghost" type="button" onClick={() => setBulk({ kind: 'merge', ids: Array.from(selectedIds) })}>Merge</button>
          )}
          {total > selectedIds.size && rows.length > 0 && rows.every(r => selectedIds.has(r.id)) && (
            <button className="gtbk-selall" type="button" disabled={selectingAll} onClick={selectAllMatching}>
              {selectingAll ? 'Selecting…' : 'Select all ' + total + ' matching'}
            </button>
          )}
          <div className="gtbn-spacer" />
          <button className="gtc-btn-ghost" type="button" onClick={() => setSelectedIds(new Set())}>Clear</button>
        </div>
      )}
      {bulk && bulk.kind === 'tag' && (
        <BulkTagModal candidateIds={bulk.ids} tags={tags} authFetch={authFetch} flash={flash} onClose={() => setBulk(null)} onDone={() => load()} />
      )}
      {bulk && bulk.kind === 'role' && (
        <BulkRoleModal candidateIds={bulk.ids} authFetch={authFetch} flash={flash} onClose={() => setBulk(null)} onDone={() => load()} />
      )}
      {bulk && bulk.kind === 'interview' && (
        <BulkInterviewModal candidateIds={bulk.ids} authFetch={authFetch} flash={flash} onClose={() => setBulk(null)} onDone={() => load()} />
      )}
      {bulk && bulk.kind === 'looking' && (
        <BulkLookingModal candidateIds={bulk.ids} authFetch={authFetch} flash={flash} onClose={() => setBulk(null)} onDone={() => load()} />
      )}
      {bulk && bulk.kind === 'merge' && (
        <MergeModal ids={bulk.ids} authFetch={authFetch} flash={flash} onClose={() => setBulk(null)}
          onDone={() => { setSelectedIds(new Set()); load(); }} />
      )}
      {bulk && bulk.kind === 'email' && ( // C1-EMAIL
        <GtEmailModal candidateIds={bulk.ids} source={{ kind: 'candidates' }} authFetch={authFetch} onClose={() => { setBulk(null); load(); }} />
      )}
      {bulk && bulk.kind === 'sequence' && ( // C1-SEQ3
        <GtSequenceStart candidateIds={bulk.ids} source={{ kind: 'candidates' }} authFetch={authFetch} navigate={navigate} onClose={() => setBulk(null)} />
      )}
      {bulk && bulk.kind === 'form' && (
        <GtSendFormModal candidateIds={bulk.ids} source={{ kind: 'candidates' }} authFetch={authFetch} onClose={() => setBulk(null)} />
      )}
      {importing && (
        <GtImportWizard entity="candidates" authFetch={enrichFetch} flash={flash}
          onClose={() => setImporting(false)} onDone={() => load()} />
      )}
      {enrichRuns.length > 0 && (
        <GtEnrichProgress runIds={enrichRuns} authFetch={enrichFetch} onFinished={() => load()} onDismiss={() => setEnrichRuns([])} />
      )}
      {enrichFor && (
        <GtEnrichModal candidateIds={enrichFor} authFetch={enrichFetch} flash={flash} onClose={() => setEnrichFor(null)}
          onStarted={r => { setSelectedIds(new Set()); if (r.runs && r.runs.length) setEnrichRuns(r.runs.map(x => x.id)); }} />
      )}
      {benchFor && (
        <GtAddToBenchModal candidateIds={benchFor} authFetch={authFetch} flash={flash} navigate={navigate}
          onClose={() => setBenchFor(null)} onDone={() => setSelectedIds(new Set())} />
      )}
      <div className="gtc-tabs">
        <button type="button" className={'gtc-tab' + (activeViewId === null ? ' gtc-tab--on' : '')}
          onClick={() => { setActiveViewId(null); setFilterTree(null); setPage(0); }}>All candidates</button>
        {savedViews.map(v => (
          <button key={v.id} type="button" className={'gtc-tab' + (activeViewId === v.id ? ' gtc-tab--on' : '')}
            onClick={() => { setActiveViewId(v.id); applyViewTree(v.tree); setPage(0); }}>{v.name}</button>
        ))}
        <button type="button" className="gtc-tab gtc-tab--add" title="New view" onClick={() => setShowFilters(true)}>+</button>
      </div>

      <div className="gtc-toolbar">
        <div className="gtc-tb-search">
          <span className="gtc-tb-searchicon">⌕</span>
          <input placeholder="Search everything: skills, CV, notes, answers… or describe who you need" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        <div className="gtc-tb-actions">
          <button className={'gtc-tb-btn' + (showColumns ? ' gtc-tb-btn--on' : '')} type="button" onClick={() => setShowColumns(v => !v)}><span className="gtc-tb-ic">▦</span>Columns</button>
          <button className={'gtc-tb-btn' + (tagMgrOpen ? ' gtc-tb-btn--on' : '')} type="button" onClick={() => setTagMgrOpen(v => !v)}><span className="gtc-tb-ic">◉</span>Tags</button>
          <button className={'gtc-tb-btn' + (filterActive ? ' gtc-tb-btn--on' : '')} type="button" onClick={() => setShowFilters(v => !v)}>
            <span className="gtc-tb-ic">⛃</span>Filters{filterActive ? ` · ${filterTree.and.length}` : ''}
          </button>
          <button className="gtc-tb-btn" type="button" title="Export current view to CSV" onClick={exportCsv}><span className="gtc-tb-ic">↧</span>Export</button>
        </div>
      </div>

      <SearchHint info={searchInfo} query={debouncedQuery} mode={searchMode} onMode={setSearchMode}
        onQuery={(t) => { setQuery(t); setDebouncedQuery(t); setSearchMode('auto'); }} /> {/* C1-AISEARCH */}

      {showColumns && (
        <div className="gtc-colpanel">
          <div className="gtc-colpanel-head">
            <span>Edit columns</span>
            <button type="button" className="gtc-colpanel-reset" onClick={() => setDraftKeys(CANDIDATE_COLUMNS.map(c => c.key))}>Reset to default</button>
          </div>
          <div className="gtc-colpanel-cols">
            <div className="gtc-colpanel-visible">
              <div className="gtc-colpanel-subhead">Visible — drag to reorder</div>
              {draftKeys.map(k => {
                const col = ALL_CANDIDATE_COLUMNS.find(c => c.key === k);
                if (!col) return null;
                const locked = col.key === 'id' || col.key === 'full_name';
                return (
                  <div key={col.key}
                    className={'gtc-colrow' + (locked ? ' gtc-colrow--locked' : '') + (dragKey === col.key ? ' gtc-colrow--dragging' : '')}
                    draggable={!locked}
                    onDragStart={() => !locked && setDragKey(col.key)}
                    onDragEnd={() => setDragKey(null)}
                    onDragOver={e => {
                      e.preventDefault();
                      if (locked || !dragKey || dragKey === col.key) return;
                      setDraftKeys(prev => {
                        const next = prev.filter(x => x !== dragKey);
                        const idx = next.indexOf(col.key);
                        next.splice(idx, 0, dragKey);
                        return next;
                      });
                    }}>
                    <span className="gtc-colgrip">{locked ? '🔒' : '⠿'}</span>
                    <span className="gtc-collabel">{col.label}</span>
                    {!locked && (
                      <button type="button" className="gtc-colremove" title="Hide column"
                        onClick={() => setDraftKeys(prev => prev.filter(x => x !== col.key))}>×</button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="gtc-colpanel-hidden">
              <div className="gtc-colpanel-subhead">Hidden — click to add</div>
              <div className="gtc-colpanel-hiddengrid">
                {ALL_CANDIDATE_COLUMNS.filter(c => !draftKeys.includes(c.key)).map(col => (
                  <button type="button" key={col.key} className="gtc-coladd" onClick={() => setDraftKeys(prev => [...prev, col.key])}>
                    <span>+</span>{col.label}
                  </button>
                ))}
                {ALL_CANDIDATE_COLUMNS.filter(c => !draftKeys.includes(c.key)).length === 0 && (
                  <div className="gtc-colpanel-allon">All columns visible</div>
                )}
              </div>
            </div>
          </div>
          <div className="gtc-colpanel-foot">
            <button type="button" className="gtc-colbtn" onClick={() => setShowColumns(false)}>Cancel</button>
            <button type="button" className="gtc-colbtn gtc-colbtn--apply" onClick={() => { setVisibleKeys(draftKeys); setShowColumns(false); }}>Apply</button>
          </div>
        </div>
      )}

      {tagMgrOpen && (
        <TagManager tags={tags} onCreate={createTag} onDelete={deleteTag} onClose={() => setTagMgrOpen(false)} />
      )}

      {error && <div className="gtc-error">{error}</div>}
      {toast && <div className="gtc-toast">{toast}</div>}

      <div className="gtc-body">
        {showFilters && (
          <div className="gtc-filterpanel">
            <GtFilterRail
              schema={CANDIDATE_SCHEMA}
              value={filterTree}
              onChange={(t) => { setFilterTree(t); setPage(0); setActiveViewId(null); }}
              matchCount={filterActive ? total : undefined}
              loading={loading}
              savedViews={savedViews}
              onSaveView={saveCurrentView}
              onApplyView={(v) => { applyViewTree(v.tree); setPage(0); setActiveViewId(v.id); }}
              onDeleteView={deleteView}
              distincts={distincts}
            />
          </div>
        )}
        <div className="gtc-tablecol">
          <GtTable
            columns={visibleKeys.map(k => ALL_CANDIDATE_COLUMNS.find(c => c.key === k)).filter(Boolean)}
            rows={rows}
            loading={loading}
            selectedIds={selectedIds}
            onToggle={toggle}
            onToggleAll={toggleAll}
            allTags={tags}
            onTagToggle={toggleTag}
            onCandidateClick={(row) => navigate(paths.candidate(row.ref || row.id))}
            sort={sort}
            onSort={onSort}
          />
        </div>
      </div>

      <div className="gtc-footer">
        <div className="gtc-pagesize">
          <span>Rows per page</span>
          <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div className="gtc-pager">
          <button disabled={safePage <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Prev</button>
          <span className="gtc-pageno">{safePage + 1} of {totalPages}</span>
          <button disabled={safePage >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>Next</button>
        </div>
        <div className="gtc-rangeinfo">
          {total === 0 ? '0' : `${safePage * pageSize + 1}\u2013${Math.min(total, safePage * pageSize + rows.length)}`} of {total}
        </div>
      </div>
    </div>
  );
}

// ---- Tag manager: create tags with name + colour, delete existing ----
const TAG_COLOURS = ['#1FA9B2', '#00A878', '#2E8FFF', '#F5A623', '#F43F5E', '#7C5CFC', '#EC4899', '#64748B', '#0F766E', '#B8860B'];

function TagManager({ tags, onCreate, onDelete, onClose }) {
  const [name, setName] = useState('');
  const [colour, setColour] = useState(TAG_COLOURS[0]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    await onCreate(name.trim(), colour);
    setName(''); setColour(TAG_COLOURS[0]); setBusy(false);
  };

  return (
    <div className="gtc-colpanel gtc-tagmgr">
      <div className="gtc-colpanel-head">
        <span>Candidate tags</span>
        <button type="button" className="gtc-colpanel-reset" onClick={onClose}>Close</button>
      </div>
      <div className="gtc-tagmgr-body">
        <div className="gtc-tagmgr-form">
          <input className="gtc-tagmgr-input" placeholder="New tag name (e.g. Shortlist)" value={name}
            onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') submit(); }} />
          <div className="gtc-tagmgr-swatches">
            {TAG_COLOURS.map(c => (
              <button key={c} type="button" className={'gtc-swatch' + (colour === c ? ' gtc-swatch--on' : '')} style={{ background: c }} onClick={() => setColour(c)} title={c} />
            ))}
          </div>
          <button type="button" className="gtc-colbtn gtc-colbtn--apply" disabled={!name.trim() || busy} onClick={submit}>Add tag</button>
        </div>
        <div className="gtc-tagmgr-list">
          {tags.length === 0 && <div className="gtc-colpanel-allon">No tags yet. Create one above.</div>}
          {tags.map(t => (
            <div key={t.id} className="gtc-tagmgr-item">
              <span className="gtc-tagchip" style={{ background: t.colour }}>{t.name}</span>
              <button type="button" className="gtc-colremove" title="Delete tag" onClick={() => onDelete(t.id)}>×</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
